import React, { useEffect, useState } from "react";
import axios from "axios";

const BusinessCategory = () => {
  const [name, setName] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  // Fetch categories from backend
  const fetchCategories = async () => {
    try {
      const res = await axios.get("https://api.vyapakprint.com/api/category-options");
      setCategories(res.data.options || res.data); // depends on your API shape
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImageFile(file);
    if (file) setPreview(URL.createObjectURL(file));
  };

  const resetForm = () => {
    setName("");
    setImageFile(null);
    setPreview(null);
    setIsEditing(false);
    setEditId(null);
    setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || (!imageFile && !isEditing)) {
      setMessage("Please provide both name and image");
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    if (imageFile) formData.append("image", imageFile);

    try {
      setLoading(true);

      if (isEditing) {
        const res = await axios.put(
          `https://api.vyapakprint.com/api/category-options/${editId}`,
          formData
        );
        setMessage("✅ Category updated");
      } else {
        const res = await axios.post(
          "https://api.vyapakprint.com/api/category-options",
          formData
        );
        setMessage("✅ Category created");
      }

      resetForm();
      fetchCategories();
    } catch (error) {
      console.error(error);
      setMessage("❌ Failed to save category");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (cat) => {
    setName(cat.name);
    setEditId(cat._id);
    setPreview(cat.image);
    setIsEditing(true);
    setImageFile(null); // Keep existing image unless user re-uploads
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      try {
        await axios.delete(`https://api.vyapakprint.com/api/category-options/${id}`);
        fetchCategories();
        alert("✅ Category deleted");
      } catch (err) {
        console.error(err);
        alert("❌ Failed to delete category");
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow rounded-lg">
      <h2 className="text-2xl font-semibold mb-4">
        {isEditing ? "Edit Category" : "Create Business Category"}
      </h2>

      {message && <p className="mb-4 text-sm text-red-600">{message}</p>}

      <form onSubmit={handleSubmit} encType="multipart/form-data">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block font-medium">Category Name</label>
            <input
              type="text"
              placeholder="Enter category name"
              className="w-full border rounded px-3 py-2 mt-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <label className="block font-medium">Category Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="mt-1"
            />
            {preview && (
              <img
                src={preview}
                alt="Preview"
                className="mt-2 h-20 w-20 object-cover rounded border"
              />
            )}
          </div>
        </div>

        <div className="mt-4 flex gap-3">
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading
              ? isEditing
                ? "Updating..."
                : "Creating..."
              : isEditing
              ? "Update Category"
              : "Create Category"}
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={resetForm}
              className="bg-gray-500 text-white px-4 py-2 rounded"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* Category Table */}
      <h3 className="text-xl mt-10 mb-3">Categories</h3>
      {categories.length === 0 ? (
        <p>No categories found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border text-sm">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="p-3">Image</th>
                <th className="p-3">Name</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => (
                <tr key={cat._id} className="border-t">
                  <td className="p-3">
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="h-12 w-12 object-cover rounded"
                    />
                  </td>
                  <td className="p-3">{cat.name}</td>
                  <td className="p-3 space-x-2">
                    <button
                      onClick={() => handleEdit(cat)}
                      className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(cat._id)}
                      className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BusinessCategory;
